import { ref, type Ref } from "vue";
import {
  DEFAULT_BODY_FONT,
  DEFAULT_HEADING_FONT,
  fontStackFor,
  isEditorFontId,
  type EditorFontId,
} from "../editor/fonts";
import { isJust, toMaybe } from "../lib/maybe";
import { readStringPref, writeStringPref } from "../lib/localPrefs";

const bodyFontId = ref<EditorFontId>(DEFAULT_BODY_FONT);
const headingFontId = ref<EditorFontId>(DEFAULT_HEADING_FONT);

function readStoredFont(
  storedM: ReturnType<typeof toMaybe<string>>,
  fallback: EditorFontId,
): EditorFontId {
  return isJust(storedM) && isEditorFontId(storedM.value)
    ? storedM.value
    : fallback;
}

function readBodyFont(): EditorFontId {
  if (typeof document === "undefined") return DEFAULT_BODY_FONT;

  const fromDom = toMaybe(document.documentElement.dataset.fontBody);
  if (isJust(fromDom) && isEditorFontId(fromDom.value)) return fromDom.value;

  const stored = readStringPref("fontBody");
  if (isJust(stored) && stored.value === "newsreader") return DEFAULT_BODY_FONT;
  return readStoredFont(stored, DEFAULT_BODY_FONT);
}

function readHeadingFont(): EditorFontId {
  if (typeof document === "undefined") return DEFAULT_HEADING_FONT;

  const fromDom = toMaybe(document.documentElement.dataset.fontHeading);
  if (isJust(fromDom) && isEditorFontId(fromDom.value)) return fromDom.value;
  if (isJust(fromDom) && fromDom.value === "newsreader")
    return DEFAULT_HEADING_FONT;

  const stored = readStringPref("fontHeading");
  if (isJust(stored) && stored.value === "newsreader")
    return DEFAULT_HEADING_FONT;
  return readStoredFont(stored, DEFAULT_HEADING_FONT);
}

function applyBodyFont(next: EditorFontId, persist = true): void {
  bodyFontId.value = next;
  document.documentElement.dataset.fontBody = next;
  document.documentElement.style.setProperty(
    "--font-prose",
    fontStackFor(next),
  );
  if (persist) {
    writeStringPref("fontBody", next);
  }
}

function applyHeadingFont(next: EditorFontId, persist = true): void {
  headingFontId.value = next;
  document.documentElement.dataset.fontHeading = next;
  document.documentElement.style.setProperty(
    "--font-heading",
    fontStackFor(next),
  );
  if (persist) {
    writeStringPref("fontHeading", next);
  }
}

export function restorePersistedFonts(): void {
  applyBodyFont(readBodyFont(), true);
  applyHeadingFont(readHeadingFont(), true);
}

/** Apply project `.zwconfig` fonts without writing UI prefs (session apply). */
export function applyProjectFonts(fonts: {
  body: string;
  heading: string;
}): void {
  if (isEditorFontId(fonts.body)) applyBodyFont(fonts.body, false);
  if (isEditorFontId(fonts.heading)) applyHeadingFont(fonts.heading, false);
}

export function useEditorFont(): {
  bodyFontId: Ref<EditorFontId, EditorFontId>;
  headingFontId: Ref<EditorFontId, EditorFontId>;
  setBodyFont: (next: EditorFontId, persist?: boolean) => void;
  setHeadingFont: (next: EditorFontId, persist?: boolean) => void;
  restorePersistedFonts: () => void;
} {
  restorePersistedFonts();

  return {
    bodyFontId,
    headingFontId,
    setBodyFont: applyBodyFont,
    setHeadingFont: applyHeadingFont,
    restorePersistedFonts,
  };
}
